/* Cart UI gate only. Requires separate Shopify validation to enforce direct checkout. */
(() => {
  'use strict';
  const settings = document.getElementById('dime-captcha-config');
  if (!settings || window.dimeCartCaptchaInstalled) return;
  let config;
  try { config = JSON.parse(settings.textContent); } catch (_) { return; }
  if (!config.enabled || !config.siteKey || !config.verifyUrl) return;
  let endpoint;
  try { endpoint = new URL(config.verifyUrl); } catch (_) { return; }
  if (endpoint.protocol !== 'https:') return;
  window.dimeCartCaptchaInstalled = true;

  const states = new WeakMap();
  let bypassButton = null;
  let bypassForm = null;
  let scanning = false;
  const selector = 'button[name="checkout"],input[name="checkout"],a[href]';
  const isCheckout = element => {
    if (!element || !element.matches(selector)) return false;
    if (element.tagName !== 'A') return true;
    const url = new URL(element.href, location.href);
    return url.origin === location.origin && /(?:^|\/)checkout\/?$/.test(url.pathname);
  };

  let apiResolve, apiReject;
  const apiReady = new Promise((resolve, reject) => { apiResolve = resolve; apiReject = reject; });
  apiReady.catch(() => {});
  window.dimeCaptchaApiReady = () => apiResolve(window.grecaptcha);
  const script = document.createElement('script');
  script.src = 'https://www.google.com/recaptcha/api.js?onload=dimeCaptchaApiReady&render=explicit';
  script.async = true;
  script.defer = true;
  script.onerror = () => apiReject(new Error('captcha-unavailable'));
  const apiTimeout = setTimeout(() => apiReject(new Error('captcha-timeout')), 15000);
  apiReady.then(() => clearTimeout(apiTimeout), () => clearTimeout(apiTimeout));
  document.head.append(script);

  function mount(button) {
    if (states.has(button)) return states.get(button);
    const panel = document.createElement('div');
    panel.className = 'dime-captcha';
    panel.style.cssText = 'display:grid;gap:8px;margin:12px 0;flex-basis:100%;max-width:100%;min-width:0;grid-column:1 / -1;justify-items:start;text-align:left';
    const label = document.createElement('p');
    label.textContent = 'Verify you’re human to continue';
    label.style.cssText = 'margin:0;font-size:14px';
    const widget = document.createElement('div');
    const message = document.createElement('p');
    message.setAttribute('role', 'status');
    message.setAttribute('aria-live', 'polite');
    message.style.cssText = 'margin:0;font-size:13px';
    panel.append(label, widget, message);
    button.before(panel);
    // Impact's compact cart form otherwise turns these into three tiny columns.
    if (button.parentElement && button.parentElement.matches('.buy-buttons--compact')) {
      button.parentElement.style.gridTemplateColumns = 'repeat(2,minmax(0,1fr))';
      button.parentElement.prepend(panel);
    }
    const state = { panel, message, token: '', widgetId: null, busy: false };
    states.set(button, state);
    apiReady.then(api => {
      if (!button.isConnected) { panel.remove(); return; }
      state.widgetId = api.render(widget, {
        sitekey: config.siteKey,
        size: panel.getBoundingClientRect().width < 310 ? 'compact' : 'normal',
        callback: token => { state.token = token; message.textContent = ''; panel.removeAttribute('data-verification-result'); },
        'expired-callback': () => { state.token = ''; message.textContent = 'Please verify again.'; },
        'error-callback': () => { state.token = ''; message.textContent = 'The security check could not load. Please refresh and try again.'; }
      });
    }).catch(() => { message.textContent = 'The security check could not load. Please refresh and try again.'; });
    return state;
  }

  function scan() {
    scanning = false;
    document.querySelectorAll(selector).forEach(button => { if (isCheckout(button)) mount(button); });
  }
  const observer = new MutationObserver(records => {
    if (scanning || !records.some(record => record.addedNodes.length)) return;
    scanning = true;
    requestAnimationFrame(scan);
  });
  observer.observe(document.body, { childList: true, subtree: true });
  scan();

  function stop(event) { event.preventDefault(); event.stopImmediatePropagation(); }

  async function verifyAndContinue(button) {
    const state = mount(button);
    if (state.busy) return;
    if (!state.token) {
      state.message.textContent = 'Complete the “I’m not a robot” check first.';
      state.panel.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const frame = state.panel.querySelector('iframe');
      if (frame) frame.focus();
      return;
    }
    state.busy = true;
    state.message.textContent = 'Verifying…';
    button.setAttribute('aria-busy', 'true');
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);
    let verified = false;
    try {
      const response = await fetch(endpoint.href, {
        method: 'POST', mode: 'cors', credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: state.token }), signal: controller.signal
      });
      const result = await response.json();
      verified = response.ok && result.verified === true;
      state.panel.setAttribute('data-verification-result', verified ? 'verified' : String(result.error || 'rejected'));
      state.message.textContent = verified ? 'Verified.' : 'Verification failed. Please try the check again.';
    } catch (_) {
      state.panel.setAttribute('data-verification-result', 'network-error');
      state.message.textContent = 'We could not complete verification. Please try again.';
    } finally {
      clearTimeout(timer);
      state.token = '';
      state.busy = false;
      button.removeAttribute('aria-busy');
      if (state.widgetId !== null) window.grecaptcha.reset(state.widgetId);
    }
    if (!verified || !button.isConnected) return;
    bypassButton = button;
    bypassForm = button.form || null;
    button.click();
    bypassButton = null;
    bypassForm = null;
  }

  document.addEventListener('click', event => {
    const button = event.target.closest && event.target.closest(selector);
    if (!isCheckout(button)) return;
    if (button === bypassButton) { bypassButton = null; return; }
    stop(event);
    verifyAndContinue(button);
  }, true);

  document.addEventListener('submit', event => {
    if (event.target === bypassForm) { bypassForm = null; return; }
    const button = event.submitter || event.target.querySelector('[name="checkout"]');
    if (!isCheckout(button)) return;
    stop(event);
    verifyAndContinue(button);
  }, true);
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    document.querySelectorAll(selector).forEach(button => {
      const state = states.get(button);
      if (state) {
        state.token = '';
        if (state.widgetId !== null) window.grecaptcha.reset(state.widgetId);
      }
    });
  });
})();
